/**
 * CraftingPanel — recipe list on the left, selected-recipe detail on the right.
 * Click a recipe to see its materials; click a material to see where to get it.
 * Craft is disabled until every material requirement is met.
 */
import { useState } from "react";
import type { HudSnapshot } from "@/engine/GameState";
import { RECIPES, type RecipeInput } from "@/game/crafting/recipes";
import { itemIcon } from "./itemIcons";

interface Props {
  snap: HudSnapshot;
  onCraft: (recipeId: string) => void;
  onClose: () => void;
}

function have(snap: HudSnapshot, inp: RecipeInput): number {
  if (inp.kind === "minerals") return snap.minerals;
  if (inp.kind === "units") return snap.units;
  return snap.inventory[inp.id ?? ""] ?? 0;
}

export default function CraftingPanel({ snap, onCraft, onClose }: Props) {
  const [sel, setSel] = useState(RECIPES[0]?.id ?? "");
  const [openMat, setOpenMat] = useState<number | null>(null);
  const recipe = RECIPES.find((r) => r.id === sel) ?? RECIPES[0];
  const canCraft = recipe
    ? recipe.inputs.every((inp) => have(snap, inp) >= inp.amount)
    : false;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="craft-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Crafting"
      >
        <div className="upg-head">
          <div className="upg-head-title">⚒ Crafting</div>
          <button className="upg-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="craft-body">
          <div className="craft-list">
            {RECIPES.map((r) => {
              const ico = itemIcon(r.outputItem);
              return (
                <button
                  key={r.id}
                  className={`craft-item${r.id === sel ? " sel" : ""}`}
                  onClick={() => {
                    setSel(r.id);
                    setOpenMat(null);
                  }}
                >
                  {ico ? (
                    <img className="craft-item-img" src={ico} alt="" />
                  ) : (
                    <span className="craft-item-icon">{r.icon}</span>
                  )}
                  <span className="craft-item-name">{r.name}</span>
                </button>
              );
            })}
          </div>

          {recipe && (
            <div className="craft-detail">
              <div className="craft-detail-head">
                {itemIcon(recipe.outputItem) ? (
                  <img
                    className="craft-detail-img"
                    src={itemIcon(recipe.outputItem)}
                    alt=""
                  />
                ) : (
                  <span className="craft-detail-icon">{recipe.icon}</span>
                )}
                <div>
                  <div className="craft-detail-name">{recipe.name}</div>
                  <div className="craft-detail-desc">{recipe.desc}</div>
                </div>
              </div>

              <div className="craft-mats-title">Materials (tap for source)</div>
              {recipe.inputs.map((inp, i) => {
                const h = have(snap, inp);
                const ok = h >= inp.amount;
                return (
                  <div key={i} className="craft-mat">
                    <button
                      className="craft-mat-row"
                      onClick={() => setOpenMat(openMat === i ? null : i)}
                    >
                      <span className="craft-mat-name">
                        {inp.kind === "item" && inp.id && itemIcon(inp.id) ? (
                          <img
                            className="craft-mat-img"
                            src={itemIcon(inp.id)}
                            alt=""
                          />
                        ) : null}
                        {inp.label}
                      </span>
                      <span className={ok ? "craft-mat-ok" : "craft-mat-no"}>
                        {h}/{inp.amount}
                      </span>
                    </button>
                    {openMat === i && (
                      <div className="craft-mat-src">📍 {inp.source}</div>
                    )}
                  </div>
                );
              })}

              <div className="craft-actions">
                <button className="hud-btn ghost" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="hud-btn accent"
                  disabled={!canCraft}
                  onClick={() => canCraft && onCraft(recipe.id)}
                >
                  Craft
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
